import * as THREE from 'three';
import { hash01 } from '@/core/rng';
import { CHUNK_SIZE, type ChunkData, type VegKind } from '@/world/chunk';
import { expressed, resolvePlant, type PlantPhenotypeSource } from '@/render/phenotype';
import { chunkHeightAt } from '@/world/env';
import { PLANT_EXPRESSION, type VegetationKit } from './vegetation';
import { dryness, DRY_GLSL, ORGANIC_SHARE } from '@/render/season';

/**
 * Chunk surface with a skirt: every border vertex drops a curtain straight
 * down, coloured like the edge it hangs from. Neighbouring chunks at different
 * LODs sample the same ground at different resolutions, so their edges cannot
 * line up vertex-for-vertex — the skirt makes the crack show terrain instead
 * of daylight. Normals come from the height field analytically; the skirt
 * copies its top vertex's normal so the curtain lights like the ground above.
 */
const SKIRT_DROP = 30;

function buildTerrainGeometry(
  res: number, height: Float32Array, colour: Float32Array, material: Float32Array,
  apron?: ChunkData['apron'], wetness?: Float32Array,
): THREE.BufferGeometry {
  const step = CHUNK_SIZE / (res - 1);
  const half = CHUNK_SIZE / 2;
  const nTop = res * res;
  const border: number[] = [];               // top-vertex index of each skirt vertex
  for (let i = 0; i < res; i++) border.push(i);                          // north
  for (let i = 0; i < res; i++) border.push((res - 1) * res + i);        // south
  for (let j = 0; j < res; j++) border.push(j * res);                    // west
  for (let j = 0; j < res; j++) border.push(j * res + res - 1);          // east

  const total = nTop + border.length;
  const pos = new Float32Array(total * 3);
  const nor = new Float32Array(total * 3);
  const col = new Float32Array(total * 3);
  const mat = new Float32Array(total * 4);
  const wet = new Float32Array(total);

  for (let j = 0; j < res; j++) for (let i = 0; i < res; i++) {
    const k = j * res + i;
    pos[k * 3] = -half + i * step;
    pos[k * 3 + 1] = height[k];
    pos[k * 3 + 2] = -half + j * step;
    // analytic normal from central differences — central at the border too,
    // through the apron: with one-sided differences two chunks disagreed
    // about the normal at the very vertices they share, and every chunk
    // boundary wore that disagreement as a lighting crease. Fall back to
    // one-sided only when no apron rode along (hand-built test data).
    let l: number, r: number, u: number, d: number, di = 2, dj = 2;
    if (apron) {
      l = i > 0 ? height[j * res + i - 1] : apron.w[j];
      r = i < res - 1 ? height[j * res + i + 1] : apron.e[j];
      u = j > 0 ? height[(j - 1) * res + i] : apron.n[i];
      d = j < res - 1 ? height[(j + 1) * res + i] : apron.s[i];
    } else {
      l = height[j * res + Math.max(0, i - 1)]; r = height[j * res + Math.min(res - 1, i + 1)];
      u = height[Math.max(0, j - 1) * res + i]; d = height[Math.min(res - 1, j + 1) * res + i];
      di = Math.min(res - 1, i + 1) - Math.max(0, i - 1);
      dj = Math.min(res - 1, j + 1) - Math.max(0, j - 1);
    }
    const dx = (r - l) / (step * di);
    const dz = (d - u) / (step * dj);
    const inv = 1 / Math.hypot(dx, 1, dz);
    nor[k * 3] = -dx * inv; nor[k * 3 + 1] = inv; nor[k * 3 + 2] = -dz * inv;
    col[k * 3] = colour[k * 3]; col[k * 3 + 1] = colour[k * 3 + 1]; col[k * 3 + 2] = colour[k * 3 + 2];
    mat[k * 4] = material[k * 4]; mat[k * 4 + 1] = material[k * 4 + 1];
    mat[k * 4 + 2] = material[k * 4 + 2]; mat[k * 4 + 3] = material[k * 4 + 3];
    wet[k] = wetness ? wetness[k] : 0;
  }
  for (let s = 0; s < border.length; s++) {
    const src = border[s], dst = nTop + s;
    pos[dst * 3] = pos[src * 3];
    pos[dst * 3 + 1] = pos[src * 3 + 1] - SKIRT_DROP;
    pos[dst * 3 + 2] = pos[src * 3 + 2];
    nor[dst * 3] = nor[src * 3]; nor[dst * 3 + 1] = nor[src * 3 + 1]; nor[dst * 3 + 2] = nor[src * 3 + 2];
    // the curtain darkens toward its hem: a hairline crack reads as crevice
    // shadow, and a coast skirt fades into the water instead of glowing
    // sand through the shallows
    col[dst * 3] = col[src * 3] * 0.6;
    col[dst * 3 + 1] = col[src * 3 + 1] * 0.6;
    col[dst * 3 + 2] = col[src * 3 + 2] * 0.6;
    // a skirt vertex wears its top's ground, so the grain never tears
    mat[dst * 4] = mat[src * 4]; mat[dst * 4 + 1] = mat[src * 4 + 1];
    mat[dst * 4 + 2] = mat[src * 4 + 2]; mat[dst * 4 + 3] = mat[src * 4 + 3];
    wet[dst] = wet[src];
  }

  const idx: number[] = [];
  for (let j = 0; j < res - 1; j++) for (let i = 0; i < res - 1; i++) {
    const k = j * res + i;
    idx.push(k, k + res, k + 1, k + 1, k + res, k + res + 1);
  }
  // skirt quads: each edge runs res vertices, in the order border[] was filled
  for (let e = 0; e < 4; e++) {
    const o = e * res;
    for (let i = 0; i < res - 1; i++) {
      const a = border[o + i], b = border[o + i + 1];
      const a2 = nTop + o + i, b2 = nTop + o + i + 1;
      idx.push(a, a2, b, b, a2, b2);
    }
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
  geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
  geo.setAttribute('aMat', new THREE.BufferAttribute(mat, 4));
  geo.setAttribute('aWet', new THREE.BufferAttribute(wet, 1));
  geo.setIndex(idx);
  geo.computeBoundingSphere();
  return geo;
}

/**
 * Fine surface relief, injected into the one terrain material.
 *
 * The height field is sampled on a 4 m lattice at its finest, so between two
 * vertices the ground is a flat triangle: at 10–200 m the land read as smooth
 * dough, which is a lighting problem rather than a geometry one. Three octaves
 * of value noise in world XZ, differentiated analytically, tilt the *fragment
 * normal* — nothing moves. The height field, the skirt, the analytic vertex
 * normals and every metre the simulation reads are untouched; this is shading.
 *
 * The band is deliberately under the lattice's own Nyquist (8 m), so the relief
 * adds detail the mesh cannot carry instead of fighting detail it already has.
 * Amplitudes are metres of imaginary relief; the slope each one contributes is
 * about 1.5·A/L, so the three together peak near a quarter — enough for the sun
 * to model a hillside, gentle enough never to read as noise.
 *
 * `RELIEF_FADE` is the other half of the idea. A 0.85 m feature is two pixels
 * at 430 m and half a pixel at 1.7 km, and half-pixel shading detail is
 * shimmer — so the strength ramps out across the band where it stops being
 * resolvable, and past it the ground is exactly as smooth as its geometry.
 */
const RELIEF_WAVELENGTH = [6.0, 2.2, 0.85] as const;   // metres
const RELIEF_AMPLITUDE = [0.34, 0.13, 0.05] as const;  // metres of relief
/** Metres from the camera over which the relief ramps from full to nothing. */
const RELIEF_FADE = [350, 1200] as const;
/**
 * …AND THE MESO-RELIEF (2026-08-17): the same idea one scale up, for the
 * ground the fine band leaves behind. Between the fine relief's fade-out at
 * 1.2 km and the height field's own 260 m cells there was nothing — the
 * 4 m lattice is bicubic over 260 m and so at 300 m to 3 km the land read as
 * dough again, which is what "the ground is flat" means at play altitude.
 * Two octaves at hummock scale — swells of thirty metres and dips of eleven,
 * a metre and a half of imaginary relief — tilt the normal so a low sun has
 * something to model out there. Shading only, exactly as the fine band:
 * nothing moves, the simulation reads no metre of it, and it fades before it
 * can shimmer, from full at 900 m to nothing past 3.4 km. Amplitudes are
 * conservative on purpose (slope ≈ 1.5·A/L ≈ 0.07 combined): a hillside that
 * suddenly grew corrugations would be the wrong kind of less flat.
 */
const MESO_WAVELENGTH = [31.0, 11.0] as const;   // metres
const MESO_AMPLITUDE = [1.5, 0.45] as const;     // metres of relief
const MESO_FADE = [900, 3400] as const;
/** Lattice cells before the noise repeats; keeps hash inputs small and exact. */
const RELIEF_PERIOD = 256;

/**
 * How strongly the relief tilts the normal, live. A uniform rather than a
 * constant so it can be dialled from the console against the real land — and
 * so its cost can be measured by switching it off without a rebuild.
 */
const reliefStrength = { value: 1 };

/**
 * The one terrain material in the world.
 *
 * It used to be one per chunk: with ~1,200 chunks live that is 1,200 identical
 * `MeshStandardMaterial`s, and the moment one of them wants an `onBeforeCompile`
 * it is also 1,200 shader *programs*, each compiled from scratch as the ring
 * streams. Built lazily on first use, and never disposed by a ChunkView — it
 * outlives every chunk that draws with it.
 */
let terrainMat: THREE.MeshStandardMaterial | null = null;

export function terrainMaterial(): THREE.MeshStandardMaterial {
  if (terrainMat) return terrainMat;
  const mat = new THREE.MeshStandardMaterial({
    vertexColors: true, roughness: 0.97, metalness: 0, side: THREE.DoubleSide,
  });
  const w = RELIEF_WAVELENGTH, a = RELIEF_AMPLITUDE;
  mat.onBeforeCompile = (sh) => {
    sh.uniforms.uRelief = reliefStrength;
    sh.uniforms.uDry = dryness;
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>',
        '#include <common>\nvarying vec3 vTerrW;\nattribute vec4 aMat;\nvarying vec4 vMat;\n' +
        'attribute float aWet;\nvarying float vWet;')
      // world position, carried whole: the relief is anchored in world XZ, so
      // it is the same relief on the same ground at every rung, and it cannot
      // slide when a cell is rebuilt at another one
      .replace('#include <begin_vertex>',
        '#include <begin_vertex>\nvTerrW = (modelMatrix * vec4(transformed, 1.0)).xyz;\nvMat = aMat;\nvWet = aWet;');
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <color_fragment>', `#include <color_fragment>
      // The season, before the grain. The grain is a luminance wobble applied
      // to whatever colour this ground is, so it has to see the CURED colour
      // or a straw meadow keeps a green stipple.
      diffuseColor.rgb = seasonTint(diffuseColor.rgb,
                                    vMat.x + vMat.y * ${ORGANIC_SHARE.toFixed(2)}, uDry);
      {
        // What this ground is MADE OF, drawn as per-pixel grain. Vertex
        // colour carries the tint at 4 m spacing; this carries the surface
        // between the vertices — blade stipple on grass, broken mottle on
        // soil, wind-rippled grain on sand, marbling and dark parting veins
        // on rock — blended by the aMat weights chunk.ts computed from the
        // same fields as the tint. World-anchored, faded out before any
        // octave can alias, and a luminance multiplier only: the palette
        // stays the measured palette.
        float gd = length(vViewPosition);
        float fineF = 1.0 - smoothstep(140.0, 480.0, gd);
        float broadF = 1.0 - smoothstep(900.0, 2600.0, gd);
        if (broadF > 0.002) {
          vec2 gp = vTerrW.xz;
          float mott = tNoise(gp / 5.3).x - 0.5;
          float grain = 0.0;
          if (vMat.x > 0.02) grain += vMat.x * (mott * 0.15 * broadF
            + (tNoise(gp / 0.42).x - 0.5) * 0.11 * fineF);
          if (vMat.y > 0.02) grain += vMat.y * ((tNoise(gp / 1.9 + 7.3).x - 0.5) * 0.12 * broadF
            + (tNoise(gp / 0.31).x - 0.5) * 0.12 * fineF);
          if (vMat.z > 0.02) {
            // ripples lie across the island's one prevailing wind (5.2, 1.6)
            vec2 rp = vec2(gp.x * 0.956 + gp.y * 0.294, (gp.y * 0.956 - gp.x * 0.294) * 6.0);
            grain += vMat.z * ((tNoise(rp / 1.4 + 3.1).x - 0.5) * 0.10 * (0.4 * fineF + 0.6 * broadF)
              + (tNoise(gp / 0.26).x - 0.5) * 0.07 * fineF);
          }
          if (vMat.w > 0.02) {
            float vein = 1.0 - abs(tNoise(gp / 2.6 + 11.7).x * 2.0 - 1.0);
            grain += vMat.w * (mott * 0.17 * broadF
              - smoothstep(0.80, 0.97, vein) * 0.15 * (0.5 * broadF + 0.5 * fineF));
          }
          diffuseColor.rgb *= 1.0 + grain;
        }
        // …AND THE GROUND UNDERFOOT (2026-08-18, Griffin: "add texture to the
        // ground so it stops looking visually flat"). Inside ninety metres the
        // grain above is a smooth stipple, and smooth is what reads as flat.
        // Two more world-anchored taps, sharpened rather than smoothed — a
        // 0.14 m speckle pushed through a step so it is grains and gaps, not
        // a wobble, and a 0.55 m clod pattern — with a hue tilt on the
        // speckle (earth in the gaps, a little yellow on the crowns of the
        // grains) so a square metre of ground has soil AND grass in it. Grass
        // and soil only, and gone by 90 m so nothing at range pays for it.
        float nearF = 1.0 - smoothstep(30.0, 90.0, gd);
        if (nearF > 0.002 && vMat.x + vMat.y > 0.05) {
          vec2 gp = vTerrW.xz;
          // two value-noise taps at incommensurate scales, one of them turned
          // 37°, summed: value noise alone peaks on its own lattice points, so
          // one tap pushed through a step is a GRID of grains 0.14 m apart —
          // the near ground read as graph paper when looked at straight down
          // (2026-08-19). The sum has no lattice to read.
          vec2 gr = vec2(gp.x * 0.80 - gp.y * 0.60, gp.x * 0.60 + gp.y * 0.80);
          float sp = tNoise(gp / 0.14 + 3.3).x * 0.58 + tNoise(gr / 0.091 + 17.1).x * 0.42;
          float clod = tNoise(gr / 0.55 + 8.9).x * 0.6 + tNoise(gp / 0.83 + 2.7).x * 0.4;
          // grains: sparse bright crowns, broad dark gaps
          float crown = smoothstep(0.55, 0.78, sp);
          float gap = 1.0 - smoothstep(0.30, 0.50, sp);
          float k = (vMat.x + vMat.y) * nearF;
          diffuseColor.rgb *= 1.0 + k * (crown * 0.16 - gap * 0.12 + (clod - 0.5) * 0.14);
          diffuseColor.rgb *= mix(vec3(1.0),
            mix(vec3(0.94, 0.90, 0.82), vec3(1.04, 1.02, 0.86), crown), k * (0.35 + 0.35 * gap));
        }
      }
      // THE THREE FREQUENCIES THE CLOSE-RANGE LOOK ASKS FOR (2026-08-18). The
      // grain above is the micro (0.26–0.42 m) and the meso (2–5 m). This is
      // the MACRO: broad patches at 137 m and 47 m — greener and mossier where
      // the field is low, drier and warmer where it is high — living on grass
      // and soil and not on sand or rock. World-anchored, so the same patch
      // is on the same ground at every rung and every visit; a luminance and
      // hue nudge of under a tenth, so a burn, a packed floor and a wet cell
      // still say what they say through it. Then the two casts that move the
      // palette off flat green toward moss, olive and earth — on the MATERIAL
      // weights, so a meadow and a soil bank tilt differently, and rock and
      // sand not at all — and last the wet channel: damp ground is darker,
      // cooler, and mud-dark in metre patches, from the world's own rain and
      // drainage (world/chunk.ts, ChunkData.wet).
      float tSheen = 0.0;
      {
        vec2 gp = vTerrW.xz;
        float gd = length(vViewPosition);
        float macro = tNoise(gp / 137.0 + 3.7).x * 0.6 + tNoise(gp / 47.0 + 12.1).x * 0.4;
        float organic = clamp(vMat.x + vMat.y * 0.7, 0.0, 1.0);
        vec3 macroTint = mix(vec3(0.94, 1.02, 0.91), vec3(1.07, 1.0, 0.85), macro);
        diffuseColor.rgb *= mix(vec3(1.0), macroTint * (1.0 + (macro - 0.5) * 0.18), organic * 0.85);
        diffuseColor.rgb *= mix(vec3(1.0), vec3(1.0, 1.0, 0.86), vMat.x);
        diffuseColor.rgb *= mix(vec3(1.0), vec3(1.05, 0.98, 0.86), vMat.y);
        float mudF = 1.0 - smoothstep(200.0, 700.0, gd);
        float mud = (1.0 - smoothstep(0.30, 0.62, tNoise(gp / 3.1 + 21.3).x)) * mudF;
        diffuseColor.rgb *= mix(vec3(1.0), vec3(0.76, 0.84, 0.90) * (1.0 - mud * 0.24), vWet * 0.65);
        // the sheen a low sun finds on damp ground: the roughness reader below
        // takes this, and takes more of it in the mud-dark patches. Faded out
        // with distance — a whole wet valley flaring at grazing incidence
        // read as overexposure, not as damp; the sheen is a near thing.
        tSheen = vWet * (0.4 + 0.45 * mud) * (1.0 - smoothstep(250.0, 900.0, gd));
      }`)
      .replace('#include <roughnessmap_fragment>', `#include <roughnessmap_fragment>
      roughnessFactor = mix(roughnessFactor, 0.70, tSheen);`)
      .replace('#include <common>', `#include <common>
      varying vec3 vTerrW;
      varying vec4 vMat;
      varying float vWet;
      uniform float uRelief;
      ${DRY_GLSL}
      // Hash of a lattice point, wrapped into a small range first. World
      // coordinates run to ±25 km, and a hash of a number that large loses its
      // low bits in a 32-bit float — which shows up as the pattern going flat
      // and stripy far from the origin. Wrapping tiles the noise instead, at
      // ${(RELIEF_PERIOD * RELIEF_WAVELENGTH[0]).toFixed(0)} m / ${(RELIEF_PERIOD * RELIEF_WAVELENGTH[1]).toFixed(0)} m / ${(RELIEF_PERIOD * RELIEF_WAVELENGTH[2]).toFixed(0)} m — three periods that share no
      // multiple, on a texture whose whole job is to be too fine to read.
      float tHash(vec2 i) {
        vec2 w = mod(i, ${RELIEF_PERIOD.toFixed(1)});
        vec3 q = fract(vec3(w.x, w.y, w.x) * 0.1031);
        q += dot(q, q.yzx + 33.33);
        return fract((q.x + q.y) * q.z);
      }
      // Value noise and its two derivatives in one pass: (value, d/dx, d/dy).
      // The derivatives are analytic, so the relief needs no extra taps and no
      // dFdx — which is what keeps three octaves affordable on ground that
      // covers most of the screen.
      vec3 tNoise(vec2 p) {
        vec2 i = floor(p), f = p - i;
        vec2 u = f * f * (3.0 - 2.0 * f);
        vec2 du = 6.0 * f * (1.0 - f);
        float a = tHash(i);
        float b = tHash(i + vec2(1.0, 0.0));
        float c = tHash(i + vec2(0.0, 1.0));
        float d = tHash(i + vec2(1.0, 1.0));
        float k1 = b - a, k2 = c - a, k3 = a - b - c + d;
        return vec3(a + k1 * u.x + k2 * u.y + k3 * u.x * u.y,
                    du.x * (k1 + k3 * u.y),
                    du.y * (k2 + k3 * u.x));
      }`)
      .replace('#include <normal_fragment_maps>', `#include <normal_fragment_maps>
      {
        // the relief follows the material: rock is craggy, soil is broken,
        // grass softens it, loose sand smooths it almost away
        float gdist = length(vViewPosition);
        float fade = uRelief * dot(vec4(0.85, 1.0, 0.55, 1.6), vMat) *
          (1.0 - smoothstep(${RELIEF_FADE[0].toFixed(1)}, ${RELIEF_FADE[1].toFixed(1)}, gdist));
        // the meso-relief: full at play altitude, gone before it can shimmer,
        // and softened on sand and grass the same way
        float meso = uRelief * dot(vec4(0.9, 1.0, 0.6, 1.3), vMat) *
          (1.0 - smoothstep(${MESO_FADE[0].toFixed(1)}, ${MESO_FADE[1].toFixed(1)}, gdist));
        // …and a fourth, close-range octave (2026-08-18): a 0.36 m grain that
        // breaks the three smooth octaves into ground rather than dune — under
        // a raking sun the smooth band read as brushed cloth. Full inside 40 m,
        // gone by 220, on the same material weighting.
        float grit = uRelief * dot(vec4(0.9, 1.0, 0.45, 1.4), vMat) *
          (1.0 - smoothstep(40.0, 220.0, gdist));
        if (fade > 0.002 || meso > 0.002) {
          // the micro-relief's own slope, in metres per metre of world XZ —
          // and its HEIGHT, kept this time (the .x the derivatives always came
          // with), because a hollow is darker than a crest whatever the sun
          // does: the cavity term below is the ambient occlusion this ground
          // can afford, one multiply on a value already in hand.
          vec2 p = vTerrW.xz;
          vec2 g = vec2(0.0);
          float hollow = 0.0;      // 0 on the crests … 1 in the hollows, per band
          if (fade > 0.002) {
            vec3 r0 = tNoise(p / ${w[0].toFixed(3)});
            vec3 r1 = tNoise(p / ${w[1].toFixed(3)} + 19.37);
            vec3 r2 = tNoise(p / ${w[2].toFixed(3)} + 41.73);
            g += (r0.yz * ${(a[0] / w[0]).toFixed(5)} + r1.yz * ${(a[1] / w[1]).toFixed(5)}
                + r2.yz * ${(a[2] / w[2]).toFixed(5)}) * fade;
            float hv = (r0.x * ${a[0].toFixed(3)} + r1.x * ${a[1].toFixed(3)} + r2.x * ${a[2].toFixed(3)})
                     / ${(a[0] + a[1] + a[2]).toFixed(3)};
            hollow += (1.0 - smoothstep(0.30, 0.62, hv)) * fade * 0.6;
          }
          if (grit > 0.002) {
            vec3 r3 = tNoise(p / 0.36 + 63.1);
            g += r3.yz * 0.05 * grit;
            hollow += (1.0 - smoothstep(0.35, 0.65, r3.x)) * grit * 0.25;
          }
          if (meso > 0.002) {
            vec3 m0 = tNoise(p / ${MESO_WAVELENGTH[0].toFixed(3)} + 5.11);
            vec3 m1 = tNoise(p / ${MESO_WAVELENGTH[1].toFixed(3)} + 27.9);
            g += (m0.yz * ${(MESO_AMPLITUDE[0] / MESO_WAVELENGTH[0]).toFixed(5)}
                + m1.yz * ${(MESO_AMPLITUDE[1] / MESO_WAVELENGTH[1]).toFixed(5)}) * meso;
            float mv = (m0.x * ${MESO_AMPLITUDE[0].toFixed(3)} + m1.x * ${MESO_AMPLITUDE[1].toFixed(3)})
                     / ${(MESO_AMPLITUDE[0] + MESO_AMPLITUDE[1]).toFixed(3)};
            hollow += (1.0 - smoothstep(0.30, 0.65, mv)) * meso * 0.45;
          }
          // the cavity: darker in the hollows, never brighter on the crests —
          // an occlusion, not a light. Capped so no hollow loses more than a
          // fifth, and lighter on sand, which has no hollows to speak of.
          diffuseColor.rgb *= 1.0 - min(hollow, 1.0) * 0.20 * dot(vec4(1.0, 1.0, 0.45, 0.9), vMat);
          // The terrain is a height field, so the honest way to add relief to it
          // is to add slope to its slope. Recover the surface's own dH/dx, dH/dz
          // from the interpolated normal, add the relief's, and rebuild — exact
          // for the ground, and stable on the skirt because a skirt vertex
          // copies the up-facing normal of the ground it hangs from.
          vec3 nw = normalize((vec4(normal, 0.0) * viewMatrix).xyz);
          float sgn = nw.y < 0.0 ? -1.0 : 1.0;      // DoubleSide: back faces are flipped
          vec3 nf = nw * sgn;
          vec2 slope = vec2(-nf.x, -nf.z) / max(nf.y, 0.20) + g;
          vec3 nb = normalize(vec3(-slope.x, 1.0, -slope.y)) * sgn;
          normal = normalize((viewMatrix * vec4(nb, 0.0)).xyz);
        }
      }`);
  };
  terrainMat = mat;
  return mat;
}

/** Drawing order, so a chunk's meshes are always built in the same sequence. */
const KINDS: readonly VegKind[] = [
  'broadleaf', 'conifer', 'shrub', 'tuft', 'spreading', 'reed', 'rosette', 'cushion',
  'columnar', 'palm', 'snag', 'fern', 'flower', 'willow',
  'succulent', 'plume', 'broom', 'creeper', 'umbel', 'krummholz', 'candle',
];

/**
 * How far a plant of each kind may lean, in radians — and why the lean lives
 * HERE rather than on `Instance`.
 *
 * Nothing in the world had a lean: every plant stood exactly plumb, and on a
 * hillside of a hundred that is the most obviously manufactured thing in the
 * frame. It is not stored on `Instance` because a lane building `world/strain.ts`
 * owns that shape this sprint and a sixth field would collide with it; and it
 * does not need to be. Lean is drawing, in exactly the sense the `variant`
 * field's own comment already establishes drawing — a hash of the plant's own
 * position, so it is the same lean at every rung, in every worker, and on
 * every machine, and nothing the simulation reads can see it.
 *
 * It is applied at EVERY rung, unlike `variant`, which the far tier collapses.
 * A lean that appeared at 768 m would be a law-8 resize in all but name; a
 * lean that is always there is simply what the plant is.
 *
 * Amplitudes: grass and mats lean freely because they are not holding
 * anything up, a shrub a fair way, a tree very little — 0.05 rad is 2.9°,
 * which at a 30 px crown displaces the top by about a pixel and a half. Enough
 * to break the plumb rank, far short of a felled forest. Snags lean most of
 * anything woody, because a dead tree is exactly the one that has stopped
 * resisting; a columnar poplar leans least, because its entire silhouette is
 * the claim that it is vertical.
 */
export const LEAN: Record<VegKind, number> = {
  broadleaf: 0.055, conifer: 0.030, spreading: 0.050, willow: 0.055,
  columnar: 0.018, palm: 0.075, snag: 0.10,
  shrub: 0.10, reed: 0.13, tuft: 0.15, fern: 0.14, flower: 0.15,
  rosette: 0.07, cushion: 0.05,
  succulent: 0.035, plume: 0.13, broom: 0.11, creeper: 0.06,
  umbel: 0.16, krummholz: 0.09, candle: 0.045,
};

/**
 * THE GROUND TIE — one soft disc under every near plant.
 *
 * A plant with nothing under it stands on the ground the way a sticker does,
 * and this world had the problem everywhere except under mortals:
 * `render/mortals.ts` is the only file that ever solved it, and its comment
 * names the exact symptom. LOD-0 vegetation does now cast a real shadow, but a
 * shadow cannot describe a *contact* at this scale — `sun.shadow.normalBias`
 * is 0.55 WORLD METRES and the map is about 0.76 m per texel at play altitude,
 * and both of those are larger than most of what stands on this hillside. The
 * disc is the instrument that works at the scale the shadow cannot reach.
 *
 * `MeshBasicMaterial` is deliberate and safe here, despite being unlit: it is
 * DARK and blended, so it multiplies the ground toward black. At midnight the
 * ground is already near-black and 30% of near-black is still near-black, so
 * this cannot become the glowing-at-midnight failure an unlit *bright*
 * material would be.
 *
 * One material for the whole world, for the same reason `terrainMaterial` is
 * one: a material per chunk is a thousand identical materials, and the moment
 * one of them is transparent it is also a thousand sort keys.
 */
let tieMat: THREE.MeshBasicMaterial | null = null;
function contactMaterial(): THREE.MeshBasicMaterial {
  if (!tieMat) {
    tieMat = new THREE.MeshBasicMaterial({
      color: 0x0a0d08, transparent: true, opacity: 0.42, depthWrite: false,
      vertexColors: true,
      polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2,
    });
  }
  return tieMat;
}

/**
 * The unit disc, and the reason it is hand-built rather than a `CircleGeometry`.
 *
 * A circle of uniform opacity does not read as contact at any size. It was
 * tried: at close range a big plant wore a flat dark ellipse with a crisp rim,
 * which the eye files as a *puddle* lying on the grass — a second object, and
 * a worse artefact than the floating it was added to cure. What sells contact
 * is the gradient, not the darkness: dense where the stem meets the ground,
 * gone by the edge of the footprint.
 *
 * So: a triangle fan whose centre vertex is opaque and whose rim vertices are
 * fully transparent, carried in an RGBA colour attribute. One geometry for the
 * whole world; the per-instance matrix scales it to each plant's footprint.
 */
let tieGeo: THREE.BufferGeometry | null = null;
function contactGeometry(): THREE.BufferGeometry {
  if (tieGeo) return tieGeo;
  const SEG = 12;
  const pos: number[] = [0, 0, 0];
  const col: number[] = [1, 1, 1, 1];
  for (let i = 0; i <= SEG; i++) {
    const a = (i / SEG) * Math.PI * 2;
    pos.push(Math.cos(a), 0, Math.sin(a));
    col.push(1, 1, 1, 0);
  }
  const idx: number[] = [];
  for (let i = 1; i <= SEG; i++) idx.push(0, i + 1, i);
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 4));
  g.setIndex(idx);
  tieGeo = g;
  return g;
}

/**
 * How wide the tie is as a multiple of the plant's own footprint, and how far
 * above the mesh it floats. The lift has to clear the mesh's own facets
 * without becoming a visible hover: the repo measured the analytic surface and
 * the drawn triangles disagreeing by 0.11 m mean and 0.42 m max, and the disc
 * dodges that entirely by seating on `chunkHeightAt` — the DRAWN height — so
 * the lift only has to beat depth precision.
 */
const TIE_SPREAD = 1.05, TIE_LIFT = 0.05;
/** Below this drawn width a tie is smaller than a pixel at any useful range. */
const TIE_MIN_R = 0.22;

/** The Three.js objects for one chunk. Created once, disposed on eviction. */
export class ChunkView {
  readonly group = new THREE.Group();
  private readonly mesh: THREE.Mesh;
  /** One instanced mesh per plant kind actually standing in this chunk. */
  private readonly veg: THREE.InstancedMesh[] = [];
  /** The ground ties, one draw for the whole chunk. Near ring only. */
  private ties: THREE.InstancedMesh | null = null;
  /**
   * Geometries this view OWNS, as opposed to the kit's shared ones.
   *
   * Only a burnt stand has any: carrying `aBurn` means the mesh needs a
   * geometry of its own to hang the instanced attribute on, and the r160 trap
   * is that `dispose()` frees GL buffers per GEOMETRY — so a wrapper sharing
   * the kit's `BufferAttribute` objects would, when disposed, delete the
   * buffers out from under every other live chunk drawing that kind.
   * `clone()` deep-copies, which makes disposal safe and makes tracking
   * mandatory: undisposed, each of these leaks a few hundred kilobytes on
   * eviction. Unburnt ground — every chunk in the shipped world — has none.
   */
  private readonly owned: THREE.BufferGeometry[] = [];

  constructor(data: ChunkData, kit: VegetationKit,
              /**
               * The plant phenotype source, when one exists (`render/phenotype.ts`).
               * Null (the shipped world) draws exactly what it always drew, at
               * exactly the old cost — every phenotype branch below is behind
               * this check. When present: stature, breadth and pigment compose
               * per INSTANCE into the matrix and colour (so they survive every
               * LOD rung, cast real shadows, and are what the pick raycasts),
               * and the structural axes land per (kind, variant) MESH through
               * the kit's uniform morph — the population grain, priced at
               * `createVegetationKit`'s phenotype note. Evaluated once, here,
               * at construction: a source change reaches the world through
               * chunk invalidation, exactly as every other ground truth does.
               */
              phenotype?: PlantPhenotypeSource | null) {
    const { res, height, colour, material } = data;
    const geo = buildTerrainGeometry(res, height, colour, material, data.apron, data.wet);

    this.mesh = new THREE.Mesh(geo, terrainMaterial());
    this.mesh.receiveShadow = true;
    // the plane is built about its own centre, so offset by half a chunk
    this.mesh.position.set(data.cx * CHUNK_SIZE + CHUNK_SIZE / 2, 0,
                           data.cz * CHUNK_SIZE + CHUNK_SIZE / 2);
    this.group.add(this.mesh);

    // Vegetation is one instanced mesh per *kind* — and, in the close ring,
    // per seeded near-tier variant of the kind. LOD 0 draws the rich builds
    // (a tree there is hundreds of pixels); every coarser rung collapses the
    // variants into the kind's one cheap far shape, so distance costs the
    // draw-call budget nothing. Empty kinds are skipped: most chunks carry a
    // handful, a coarse rung usually carries only the big trees, and a
    // clearing carries none at all.
    if (data.vegetation.length) {
      const nearTier = data.lod === 0;
      const variantOf = (inst: { kind: VegKind; variant?: number }): number =>
        nearTier ? (inst.variant ?? 0) % kit.near[inst.kind].length : 0;
      const count = new Map<string, number>();
      for (const inst of data.vegetation) {
        const key = `${inst.kind}:${variantOf(inst)}`;
        count.set(key, (count.get(key) ?? 0) + 1);
      }
      const m = new THREE.Matrix4(), q = new THREE.Quaternion();
      const tilt = new THREE.Quaternion(), axis = new THREE.Vector3();
      const v = new THREE.Vector3(), s = new THREE.Vector3(), c = new THREE.Color();
      const up = new THREE.Vector3(0, 1, 0);
      /**
       * The lean's two numbers, hashed from the plant's own position — see
       * `LEAN`. Two independent hashes rather than byte-slices of one: an
       * angle and a magnitude that correlate would put every plant on a
       * hillside leaning the same way, which is a wind, not a lean, and a
       * wind that never changes is worse than plumb.
       */
      const leanOf = (inst: { kind: VegKind; x: number; z: number }): number =>
        hash01(Math.round(inst.x * 3), Math.round(inst.z * 3), 0x1ea4) * LEAN[inst.kind];
      const leanDir = (inst: { x: number; z: number }): number =>
        hash01(Math.round(inst.z * 3), Math.round(inst.x * 3), 0x0d19) * Math.PI * 2;
      for (const kind of KINDS) {
        const variants = nearTier ? kit.near[kind].length : 1;
        for (let vi = 0; vi < variants; vi++) {
          const key = `${kind}:${vi}`;
          const n = count.get(key) ?? 0;
          if (n === 0) continue;
          const im = new THREE.InstancedMesh(
            nearTier ? kit.near[kind][vi] : kit.geometry[kind], kit.material, n);
          im.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(n * 3), 3);
          /*
           * THE BURN, per instance — and allocated only where one happened.
           *
           * A scar's edge runs THROUGH a stand: the plants either side of it
           * share this mesh, so how burnt a plant is cannot be a uniform and
           * cannot be a property of the geometry. It is two floats an
           * instance, and `world/overlay.ts` is the only thing that ever
           * writes them.
           *
           * Unburnt ground allocates nothing. An unbound attribute reads
           * (0,0,0,1) in GL, so a mesh with no `aBurn` draws char 0 — which
           * the shader's morph makes a bit-exact no-op. Every chunk in the
           * shipped world is that mesh.
           */
          const anyBurn = data.vegetation.some(
            (i2) => i2.kind === kind && variantOf(i2) === vi && (i2.char ?? 0) > 0);
          let burnArr: Float32Array | null = null;
          if (anyBurn) {
            burnArr = new Float32Array(n * 2);
            const own = im.geometry.clone();
            own.setAttribute('aBurn', new THREE.InstancedBufferAttribute(burnArr, 2));
            im.geometry = own;
            this.owned.push(own);
          }
          // …and the shadow eats the same crown the eye does
          im.customDepthMaterial = kit.depthMaterial;
          let i = 0;
          let rep: { x: number; z: number } | null = null;
          for (const inst of data.vegetation) {
            if (inst.kind !== kind || variantOf(inst) !== vi) continue;
            q.setFromAxisAngle(up, inst.rot);
            // …then tip it off plumb about a horizontal axis. Post-multiplying
            // keeps the plant's own turn intact: it is the same plant, leaning.
            const a = leanDir(inst);
            q.multiply(tilt.setFromAxisAngle(
              axis.set(Math.cos(a), 0, Math.sin(a)), leanOf(inst)));
            // The per-plant phenotype axes: expression factors on the drawn
            // scale and colour, neutral = exactly 1. The world's `Instance` is
            // untouched — generation, the wear/keep thresholds and every law
            // that reads the nominal scale still read the nominal scale; this
            // is expression, at draw time, which is the whole architecture.
            let fS = 1, fB = 1, fL = 1, fW = 1;
            if (phenotype) {
              const pp = phenotype(inst.kind, inst.x, inst.z);
              if (pp) {
                const e = PLANT_EXPRESSION[kind];
                fS = expressed(pp.stature ?? 0, e.stature);
                fB = expressed(pp.breadth ?? 0, e.breadth);
                fL = expressed(pp.light ?? 0, e.light);
                fW = expressed(pp.warmth ?? 0, e.warmth);
              }
            }
            m.compose(v.set(inst.x, inst.y, inst.z), q,
                      s.set(inst.scaleXZ * fB, inst.scaleY * fS, inst.scaleXZ * fB));
            im.setMatrixAt(i, m);
            if (fL === 1 && fW === 1) {
              im.setColorAt(i, c.setRGB(inst.colour[0], inst.colour[1], inst.colour[2]));
            } else {
              // warmth rotates red against blue (a value change is light's
              // job); the cap holds a pale phenotype at the world's own
              // brightest, so no albedo blows out at the midday gate
              im.setColorAt(i, c.setRGB(
                Math.min(1, inst.colour[0] * fL * fW),
                Math.min(1, inst.colour[1] * fL),
                Math.min(1, inst.colour[2] * fL / fW)));
            }
            if (burnArr) {
              burnArr[i * 2] = inst.char ?? 0;
              burnArr[i * 2 + 1] = inst.charAge ?? 0;
            }
            if (!rep) rep = { x: inst.x, z: inst.z };
            i++;
          }
          // …and the structural axes, at the mesh's own grain: priced for the
          // first plant of the stand (deterministic — the vegetation array's
          // order is) and frozen onto the mesh. Neutral prices to null and
          // hangs nothing on the mesh at all.
          if (phenotype && rep) {
            const sv = kit.phenoStructural(kind, resolvePlant(phenotype(kind, rep.x, rep.z)));
            if (sv) kit.phenoApply(im, sv);
          }
          im.instanceMatrix.needsUpdate = true;
          if (im.instanceColor) im.instanceColor.needsUpdate = true;
          // Cull these. Every one of them used to draw whether it was on screen
          // or not, and a chunk's plants are two to four instanced draws apiece —
          // over a thousand calls a frame spent on ground behind the camera, which
          // is also the budget the graded LOD ladder needs in order to keep an
          // understory further out. InstancedMesh.computeBoundingSphere() unions
          // the instances' own bounds, and the matrices are already world-space,
          // so the sphere is exact; the slack covers the wind sway, which pushes
          // a crown at most a third of a metre out of its own footprint.
          im.computeBoundingSphere();
          if (im.boundingSphere) im.boundingSphere.radius += 1.5;
          im.frustumCulled = true;
          im.receiveShadow = true;
          /*
           * …and, at LOD 0 only, it CASTS.
           *
           * The shadow map was doing nothing for this world. `shadowExtent`
           * has tracked the camera since it was written — 0.76 m per texel at
           * 300 m altitude, which its own comment says is "what lets close
           * ground read as lit rather than pasted together" — and then the
           * only object in the whole scene flagged as a caster was a cloud.
           * Twenty-six clouds, and nothing else: not a tree, not a rock, not a
           * shelter, not a person. That is the single reason everything in
           * this world looks laid ON the ground rather than standing in it.
           *
           * LOD 0 alone, and that bound is the whole cost control. The far
           * canopy is 6.7 MILLION triangles across the island and casting it
           * would be a second full pass over the largest thing in the scene
           * for shadows nobody can resolve at that range; the coarse rungs are
           * the same argument at smaller scale. The near ring is what a
           * shadow can actually describe.
           */
          im.castShadow = nearTier;
          // named for the kind alone — the pick reads a species off this name,
          // and a variant is a wardrobe, not a species
          im.name = `veg-${kind}`;
          this.veg.push(im);
          this.group.add(im);
        }
      }

      // …and the ties, near ring only — the same bound `castShadow` uses, and
      // for the same reason: past LOD 0 a tie is well under a pixel, so it
      // would cost a transparent draw per chunk to darken nothing.
      if (nearTier) {
        const wide = data.vegetation
          .filter((inst) => inst.scaleXZ * TIE_SPREAD >= TIE_MIN_R);
        if (wide.length) {
          const ties = new THREE.InstancedMesh(
            contactGeometry(), contactMaterial(), wide.length);
          const tm = new THREE.Matrix4(), tq = new THREE.Quaternion();
          const tn = new THREE.Vector3(), tv = new THREE.Vector3(), ts = new THREE.Vector3();
          const UP = new THREE.Vector3(0, 1, 0);
          let i = 0;
          for (const inst of wide) {
            // Seat on the DRAWN mesh, not the analytic field the plant itself
            // uses. That disagreement is the float/sink the ties exist to hide,
            // so a tie that inherited it would draw the bug it is covering.
            const y = chunkHeightAt(data, inst.x, inst.z);
            // …and lie ALONG the ground. A flat disc on a 12° median slope
            // stands proud on the uphill rim by most of its own radius, which
            // reads as a hovering coin — the failure it is meant to cure.
            const d = 2;
            const gx = (chunkHeightAt(data, inst.x + d, inst.z)
                      - chunkHeightAt(data, inst.x - d, inst.z)) / (2 * d);
            const gz = (chunkHeightAt(data, inst.x, inst.z + d)
                      - chunkHeightAt(data, inst.x, inst.z - d)) / (2 * d);
            tq.setFromUnitVectors(UP, tn.set(-gx, 1, -gz).normalize());
            const r = inst.scaleXZ * TIE_SPREAD;
            tm.compose(tv.set(inst.x, y + TIE_LIFT, inst.z), tq, ts.set(r, 1, r));
            ties.setMatrixAt(i++, tm);
          }
          ties.instanceMatrix.needsUpdate = true;
          ties.computeBoundingSphere();
          ties.frustumCulled = true;
          ties.name = 'veg-ties';
          // never a caster and never a receiver: it is a mark ON the ground,
          // and a shadow of a shadow is a smudge
          ties.castShadow = false;
          ties.receiveShadow = false;
          // drawn after the terrain it darkens
          ties.renderOrder = 1;
          this.ties = ties;
          this.group.add(ties);
        }
      }
    }
  }

  dispose() {
    this.mesh.geometry.dispose();
    // the burnt stands' own geometries; see `owned`
    for (const g of this.owned) g.dispose();
    // The terrain material and the kit's geometries and material are shared by
    // every chunk in the world and are never disposed here — this view owns its
    // terrain geometry and nothing else, and InstancedMesh.dispose() releases
    // only the per-instance matrix and colour buffers it allocated.
    for (const im of this.veg) im.dispose();
    this.veg.length = 0;
    // the tie geometry and material are shared like the kit's, so this
    // releases only this chunk's instance buffer
    this.ties?.dispose();
    this.ties = null;
    this.group.clear();
  }
}
